package com.neurobs.ccabserver.administrator.components

import com.neurobs.ccab.SessionConfig.StudyConfig
import com.neurobs.ccab.db.TestRunManager.TestRunEvent
import com.neurobs.ccab.{AccountSubjectResponse, AccountUserDataRest, CCABServer, LocationType, LogfileMetadataData, MetadataItem, Parameter, ParameterType, ParameterValue, ResumeManagedSessionData, SessionConfig, SetSessionData, StudySessionManager, SubjectIdentityData, TestPackageData, TestSessionCreateData, TestSessionParameter, TestSessionProperties, TestSessionResponse, VersionNumber, db}
import com.neurobs.ccab.db.{AccountData, StudyData, StudyTypeData, UserAccountViewData, UserData}
import com.neurobs.ccabserver.administrator.controller.DataPasswordCache
import com.neurobs.react.{Button, ButtonSize, CircuitState, ControlWrapperFactory, ControlWrapperFactoryControlled, ControlWrapperFactoryReact, Edit, EditControlWrapper, FormDialog, FormProcessResult, FormProcessResultValue, FormProcessor, FormProcessorRequest, FormTable, FormTableRow, GapSize, HoriAlign, LTCell, LTRow, LayoutTable, ModalContainer, Panel, Radio, RadioState, Select, SelectControlled, SelectState, SimpleActionHandler, StyleUtil}
import com.neurobs.errors.{Result, _}
import com.neurobs.util.{Guid, JsonPrinter, Logger, SecondsTimestamp}
import diode._
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks.useEffect
import slinky.web.html.{div, span, style}
import io.circe.generic.auto._

import scala.concurrent.ExecutionContext.Implicits.global

/**
 * Handles the "Configure New Session" dialog panel in Examiner.
 */
object SessionConfigureDialog {

	/** This is a hack until the OpenTok maximum message size issues can be addressed in Test Station */
	val maxSubjectNameLength = 25

	/**
	 * Immutable data provided to this control
	 */
	case class Resources
	(
		server: CCABServer,
		adminUser: AccountUserDataRest,
		studyTypes: Seq[StudyTypeData],
		stationName: String,
		stationVersion: VersionNumber,
		dataPasswordCache: DataPasswordCache,
		studySessions: StudySessionManager,
		logger: Logger
	) {
		def dataPasswordSet: Boolean = dataPassword.isDefined
		def dataPassword: Option[String] = dataPasswordCache.storedValue(adminUser.account.id)
	}

	/**
	 *  Controls displayed once a group has been selected
	 */
	case class GroupSettings
	(
		name: String,
		restrictions: Seq[StudySessionManager.DisplayNameParameterRestriction],
		sequence: SelectState[String, String],
		parameters: Seq[(Parameter, SelectState[String, String])]
	)

	case class ManagedCreateData
	(
		sessionCreateData: TestSessionCreateData,
		identity: Option[SubjectIdentityData],
		testRunHistory: Seq[TestRunEvent]
	)

	/**
	 * @param resources Resources provided to this control
	 * @param packageData Test package data sent by Test Station (after this control is created)
	 * @param resumableSubjectGuids Subject GUIDs for resumable sessions sent by Test Station (after this control is created)
	 * @param previousSessions Previous sessions populated when a subject is selected
	 * @param actionPendingDataPassword If the modal data password dialog is currently displayed, and the action
	 *                                  that should be done if the password is successfully obtained.
	 * @param isStandardType Radio boxes for standard type sessions (true) or "review/qa" sessions (false)
	 * @param selectingSubject If the subject selection modal dialog is currently displayed
	 * @param subject The selected subject
	 * @param localSubjectName Name of subject if required
	 * @param study Study selection, options populated when the control is mounted
	 * @param group the group selected, options populated when packageData is provided
	 * @param groupSettings Group-specific controls
	 */
	case class Model
	(
		resources: Resources,
		packageData: Option[TestPackageData] = None,
		resumableSubjectGuids: Seq[Guid] = Seq(),
		previousSessions: Option[Seq[db.SessionData]] = None,
		studySessions: Option[StudySessionManager.SubjectSessionHistory] = None,
		session: SelectState[SessionConfig.Session, SessionConfig.Session],
		actionPendingDataPassword: Option[Action] = None,
		locationType: SelectState[LocationType.Value, LocationType.Value],
		isStandardType: RadioState[Boolean] = RadioState.booleanRadio(value = true, trueCaption = "standard", falseCaption = "review/QA"),
		selectingSubject: Boolean = false,
		subject: Option[AccountSubjectResponse] = None,
		subjectRunHistory: Option[Seq[TestRunEvent]] = None,
		localSubjectName: EditControlWrapper,
		study: SelectState[StudyData, StudyData],
		group: SelectState[String, String],
		groupSettings: Option[GroupSettings] = None
	) {
		def namedSubjectSelected: Boolean = subject.exists(_.identity.exists(_.lastName.isDefined))
		def enteredNameRequired: Boolean = !isStandardType.value || (subject.isDefined && !namedSubjectSelected)
		def studySelected: Boolean = study.value.isDefined
		def subjectSelected: Boolean = subject.isDefined
		/** Validates and packages settings when "Start" button is pressed */
		def makeCreateData: Result[(SetSessionData, Option[ManagedCreateData], LogfileMetadataData)] = {
			if (!isStandardType.value) {
				for {
					_ <- toResult(!enteredNameRequired || localSubjectName.getValue().nonEmpty, error = "Please enter a subject name.")
					groupName <- toResult(group.value, noValueMessage = "Please select a group.")
					gSettings <- toResult(groupSettings, noValueMessage = "The group settings were not found.")
					sequenceName <- toResult(gSettings.sequence.value, noValueMessage = "Please select a sequence.")
					parameters <- sequence(gSettings.parameters.map(p => toResult(p._2.value.map(v => ParameterValue(name = p._1.name, value = v)), noValueMessage = s"Please select a value for ${p._1.name}.")))
				} yield {
					val setData = SetSessionData(
						group_name = groupName,
						configuration = None,
						parameters = parameters.toList,
						sequence_name = sequenceName,
						subject_name = (if (enteredNameRequired) localSubjectName.value else subject.map(_.displayFullName).getOrElse("Unknown")).take(maxSubjectNameLength)
					)
					(setData, None, LogfileMetadataData())
				}
			}
			else {
				val res = for {
					pData <- toResult(packageData, noValueMessage = "Test package data has not be received from the test system.")
					_ <- toResult(resources.stationVersion >= VersionNumber("0.003.000").get, error = "Regular type sessions require at least Test Station version 0.003.000.")
					_ <- toResult(subject.nonEmpty, error = "Please select a subject.")
					_ <- toResult(!enteredNameRequired || localSubjectName.getValue().nonEmpty, error = "Please enter a subject name.")
					_ <- toResult(study.value.nonEmpty, error = "Please select a study.")
					session <- toResult(session.value, noValueMessage = "Please select a session.")
					gSettings <- toResult(groupSettings, noValueMessage = s"The test package being used by this Test Station does not contain the sequence (${session.group_name},${session.sequence_name}) required by the selected session. Either upgrade the test package, or use a different session (if possible.)")
					parameters <- sequence(gSettings.parameters.map(p => toResult(p._2.value.map(v => ParameterValue(name = p._1.name, value = v)), noValueMessage = s"Please select a value for ${p._1.name}.")))
					group <- toResult(pData.groups.find(_.name == session.group_name), noValueMessage = "The group data could not be found.")
					location <- toResult(locationType.value, noValueMessage = "Please select a location type.")
				} yield {
					val setData = SetSessionData(
						group_name = session.group_name,
						configuration = None,
						parameters = parameters.toList,
						sequence_name = session.sequence_name,
						subject_name = (if (enteredNameRequired) localSubjectName.value else subject.map(_.displayFullName).getOrElse("Unknown")).take(maxSubjectNameLength)
					)
					val studyValue = study.value.get
					val subjectValue = subject.get
					val runHistory = subjectRunHistory.getOrElse(Seq())

					val props = TestSessionProperties(
						group = setData.group_name,
						sequence = setData.sequence_name,
						label = Some(session.label),
						config = setData.configuration.map(group.configs(_)),
						params = setData.parameters.map(p => TestSessionParameter(name = p.name, value = p.value))
					)
					val data = TestSessionCreateData(
						accountId = resources.adminUser.account.id,
						studyId = studyValue.id,
						accountSubjectId = subjectValue.id,
						adminUserId = Some(resources.adminUser.user.id),
						stationName = resources.stationName,
						properties = Some(JsonPrinter.jsonPrint(props)),
						startTime = SecondsTimestamp.now(),
						locationType = Some(location)
					)
					(setData, Some(ManagedCreateData(sessionCreateData = data, identity = subjectValue.identity, testRunHistory = runHistory)), LogfileMetadataData(items = Seq(
						MetadataItem(name = "account", value = resources.adminUser.account.organization),
						MetadataItem(name = "account_id", value = resources.adminUser.account.accountUuid.noBracketsString),
						MetadataItem(name = "study", value = studyValue.displayName),
						MetadataItem(name = "study_id", value = studyValue.studyUuid.noBracketsString),
						MetadataItem(name = "study_session", value = session.label),
						MetadataItem(name = "study_session_id", value = session.id.noBracketsString),
						MetadataItem(name = "reporting_id", value = subjectValue.reportingId.noBracketsString),
						MetadataItem(name = "test_package_ver", value = pData.test_ver.getOrElse("0.000.000")),
						MetadataItem(name = "examiner_id", value = resources.adminUser.user.userUuid.noBracketsString),
						MetadataItem(name = "test_station_name", value = resources.stationName),
						MetadataItem(name = "location_type", value = location.toString),
						MetadataItem(name = "study_session_ver", value = resources.studySessions.config.version)
					)))
				}
				res// match {
				//	case Left(_) => res
				//	case Right(_) => Left(res.toString)
				//}
			}
		}
		def logger: Logger = resources.logger
		def updatedGroupSettings(groupNameOpt: Option[String], sequenceNameOpt: Option[String], history: Option[StudySessionManager.SubjectSessionHistory]): Option[GroupSettings] = {
			for {
				groupName <- groupNameOpt
				data <- packageData
				group <- data.groups.find(_.name == groupName)
				if sequenceNameOpt.forall(group.seqs.contains(_))
			} yield {
				val restrictions = history.map(_.combinedRestrictions).getOrElse(Seq())
				val settings =
					if (groupSettings.exists(s => s.name == groupName && s.restrictions == restrictions)) groupSettings.get
					else {
						resources.logger.debug(s"updatedGroupSettings restrictions: $restrictions")
						val parameters = group.params.filter(_.`type` == ParameterType.Standard) map { p =>
							val values = restrictions.find(_.displayName == p.name).map(_.restriction.values.intersect(p.values)).getOrElse(p.values)
							val value = if (values.size == 1) Some(values.head) else p.default.flatMap(v => values.find(_ == v))
							val s = SelectState.simpleWithValue(items = values, value = value, noSelectOption = true)(ControlWrapperFactoryControlled)
							(p, s)
						}
						val sequence = SelectState.simple(items = group.seqs, selectIndex = None, noSelectOption = true)(ControlWrapperFactoryControlled)
						GroupSettings(groupName, restrictions, sequence, parameters)
					}
				settings.sequence.setSelectionValue(sequenceNameOpt, _ == _)
				settings
			}
		}
		def setGroupSelection(name: Option[String]): Unit = {
			group.setSelectionValue(name, _ == _)
		}
		def updatePreviousSessions(previous: Seq[db.SessionData]): Model = {
			val newStudySessions = for {
				study <- study.value
				config = study.config.flatMap(JsonPrinter.fromJson[StudyConfig]).getOrElse(resources.studySessions.config.defaultStudyConfig)
				_ = resources.logger.debug(s"updatePreviousSessions config: $config")
				history <- resources.studySessions.getSessionSuggestion(now = SecondsTimestamp.now(), studyId = study.id, studyConfig = config, tsSessions = previous)
			} yield history
			resources.logger.debug(s"updatePreviousSessions newStudySessions: $newStudySessions")
			val sessionItems = newStudySessions.map(_.protocol.sessions).getOrElse(Seq())
			val selection = newStudySessions.flatMap(_.suggestionIndex)
			resources.logger.debug(s"updatePreviousSessions selection: $selection")
			val studySession = newStudySessions.flatMap(_.suggestion.flatMap(_.studySession.map(_.studySession)))
			val newGroupSettings = updatedGroupSettings(studySession.map(_.group_name), studySession.map(_.sequence_name), newStudySessions)
			setGroupSelection(studySession.map(_.group_name))
			val sessionSelect = session.withNewItems(newItems = sessionItems, initialSelection = selection)
			resources.logger.debug(s"updatePreviousSessions sessionSelect.value: ${sessionSelect.value}")
			copy(previousSessions = Some(previous), studySessions = newStudySessions, session = sessionSelect, groupSettings = newGroupSettings)
		}
	}

	object Model {
		def apply(resources: Resources)(implicit factory: ControlWrapperFactory): Model = {
			Model(
				resources = resources,
				localSubjectName = factory.edit(initialValue = ""),
				session = SelectState.typed[SessionConfig.Session](items = Seq(), value = None, noSelectOption = true, toCaption = _.description)(ControlWrapperFactoryControlled),
				locationType = SelectState.typed(items = LocationType.values.toSeq, value = None, toCaption = _.toString),
				study = SelectState.typed[StudyData](items = Seq(), value = None, noSelectOption = true, toCaption = _.displayName),
				group = SelectState.simple(items = Seq(), selectIndex = None, noSelectOption = true)
			)
		}
	}

	case class SetDataPassword(password: Option[String]) extends Action
	case class SetIsStandardType(index: Int) extends Action
	case object SelectSubject extends Action
	case class SetSubject(subject: Option[AccountSubjectResponse]) extends Action
	case class SetStudies(studies: Seq[StudyData]) extends Action
	case class SetPackageData(data: Option[TestPackageData]) extends Action
	case class SetResumableSubjectGuids(guids: Seq[Guid]) extends Action
	case class SetPreviousSessions(sessions: Seq[db.SessionData]) extends Action
	case class SetSubjectRunHistory(runs: Seq[TestRunEvent]) extends Action
	case object SessionChange extends Action
	case object GroupChange extends Action
	case object StudyChange extends Action
	case object Clear extends Action
	case object ParameterChange extends Action

	class AppCircuit(val initialModel: Model) extends Circuit[Model] {
		outer =>
		private val generalHandler = new SimpleActionHandler[Model] {
			def handle = {
				case Clear =>
					value.localSubjectName.setValue("")
					value.locationType.setSelection(None)
					value.group.setSelection(None)
					Some(value.copy(session = value.session.withNewItems(Seq(), None), previousSessions = None, studySessions = None, subjectRunHistory = None, subject = None, groupSettings = None))
				case SetDataPassword(password) =>
					password.foreach(value.resources.dataPasswordCache.setValue(value.resources.adminUser.account.id, _))
					Some(value.copy(actionPendingDataPassword = None))
				case SetIsStandardType(index) =>
					Some(value.copy(isStandardType = value.isStandardType.newSelection(index)))
				case SelectSubject =>
					if (value.resources.dataPasswordSet) Some(value.copy(selectingSubject = true))
					else Some(value.copy(actionPendingDataPassword = Some(SelectSubject)))
				case SetSubject(subject) =>
					value.logger.debug(s"SetSubject: $subject")
					subject match {
						case None => Some(value.copy(selectingSubject = false))
						case Some(s) =>
							value.resources.server.getAccountSubjectTestSessions(s.id)._2.map {
								case Right(sessions) => outer.dispatch(SetPreviousSessions(sessions))
								case _ =>
							}
							value.resources.server.getAccountSubjectTestRunHistory(accountSubjectId = s.id)._2.map {
								case Right(history) => outer.dispatch(SetSubjectRunHistory(history))
								case _ =>
							}
							Some(value.copy(selectingSubject = false, subject = Some(s), previousSessions = None, studySessions = None, subjectRunHistory = None))
					}
				case SetStudies(studies) =>
					Some(value.copy(study = value.study.withNewItems(studies, None)))
				case SetPackageData(data) =>
					Some(value.copy(
						packageData = data,
						group = value.group.withNewItems(data.map(_.groups.map(_.name)).getOrElse(Seq()), None)
					))
				case SetResumableSubjectGuids(guids) =>
					Some(value.copy(resumableSubjectGuids = guids))
				case SetPreviousSessions(sessions) =>
					Some(value.updatePreviousSessions(sessions))
				case SetSubjectRunHistory(history) =>
					Some(value.copy(subjectRunHistory = Some(history)))
				case StudyChange =>
					value.previousSessions match {
						case Some(s) => Some(value.updatePreviousSessions(s))
						case None => Some(value.copy())
					}
				case GroupChange =>
					Some(value.copy(groupSettings = value.updatedGroupSettings(value.group.value, None, value.studySessions)))
				case SessionChange =>
					val s = value.session.value
					value.resources.logger.debug(s"SessionChange s: $s")
					value.setGroupSelection(s.map(_.group_name))
					Some(value.copy(groupSettings = value.updatedGroupSettings(s.map(_.group_name), s.map(_.sequence_name), value.studySessions)))
				case ParameterChange =>
					Some(value.copy())
			}
		}
		override val actionHandler: HandlerFunction = composeHandlers(generalHandler)
	}

	// This is a hack to allow the non-react clients to change the internal state
	case class DispatchHolder(var dispatch: Option[Dispatcher] = None) {
		def send(a: Action): Unit = dispatch.foreach(_.dispatch(a))
	}

	case class ManagedStartData
	(
		sessionData: TestSessionResponse,
		identity: Option[SubjectIdentityData],
		testRunHistory: Seq[TestRunEvent]
	)

	case class StartData
	(
		setData: SetSessionData,
		session: Option[ManagedStartData],
		metadata: LogfileMetadataData
	)

	@react object ParameterSelectView {
		case class Props
		(
			groupName: String,
			parm: Parameter,
			select: SelectState[String, String],
			history: Option[StudySessionManager.SubjectSessionHistory],
			dispatcher: Dispatcher
		)
		case class Advice
		(
			color: Option[String],
			caption: String
		) {
			def getStyle = color match {
				case None => StyleUtil.makeStyle()
				case Some(c) => StyleUtil.makeStyle(("color", c))
			}
		}
		val component = FunctionalComponent[Props] { props =>
			val advice: Option[Advice] = {
				if (props.select.items.size == 1) None
				else props.history flatMap { sessions =>
					val previous = sessions.previousParameterValues(group = props.groupName, displayName = props.parm.name)
					if (previous.isEmpty) None
					else if (previous.size == 1) Some(Advice(
						color = if (props.select.value.exists(_ != previous.head)) Some("red") else None,
						caption = s"Previous: ${previous.head}"
					))
					else Some(Advice(color = Some("red"), caption = s"Previous conflicting"))
				}
			}
			val box = SelectControlled(props.select.generateProperties(), emWidth = Some(boxWidth), onSelectionChange = () => props.dispatcher.dispatch(ParameterChange))
			advice match {
				case None => box
				case Some(a) =>
					LayoutTable(gapSize = GapSize.Small, fullWidth = false)(
						LTRow(
							LTCell(align = HoriAlign.Left)(box),
							LTCell(align = HoriAlign.Left)(span(style := a.getStyle)(a.caption))
						)
					)
			}
		}
	}

	@react object View {
		case class Props
		(
			resources: Resources,
			dispatchHolder: DispatchHolder,
			onResumeSession: (ResumeManagedSessionData, Seq[TestRunEvent]) => Unit,
			onStart: StartData => Unit
		)

		val component = FunctionalComponent[Props] { props =>
			val state = CircuitState[Model](() => Model(props.resources)(ControlWrapperFactoryReact), new AppCircuit(_))
			val model = state.value

			useEffect(() => {
				props.resources.server.getStudies(props.resources.adminUser.account.id)._2 map {
					case Right(s) => state.dispatch(SetStudies(s))
					case _ =>
				}
			}, Seq())

			props.dispatchHolder.dispatch = Some(state.dispatcher)

			def process(): Result[Either[FormProcessResult, FormProcessor]] = {
				model.makeCreateData map { data =>
					data._2 match {
						case None => Left(FormProcessResultValue(StartData(data._1, None, data._3)))
						case Some(createData) =>
							val request = props.resources.server.createTestSession(createData.sessionCreateData)
							Right(FormProcessorRequest[StartData]((request._1, request._2.map(_.map(s =>
								StartData(
									setData = data._1,
									session = Some(ManagedStartData(sessionData = s, identity = createData.identity, testRunHistory = createData.testRunHistory)),
									metadata = data._3))))))
					}
				}
			}
			def onSuccess(res: FormProcessResult): Unit = {
				props.onStart(res.asInstanceOf[FormProcessResultValue[StartData]].value)
			}
			def resumeSession(s: db.SessionData): Unit = {
				for (
					sub <- model.subject;
					stud <- model.study.items.find(_.id == s.studyId)
				) {
					props.onResumeSession(ResumeManagedSessionData(
						test_session_id = s.id,
						study_name = stud.displayName,
						access_guid = s.accessGuid,
						subject_guid = s.subjectGuid,
						reporting_id = sub.reportingId,
						account_subject_id = s.accountSubjectId,
						subject_name = sub.displayFullName.take(maxSubjectNameLength)
					), model.subjectRunHistory.getOrElse(Seq()))
				}
			}

			val studySelected = model.studySelected
			val standardType = model.isStandardType.value

			val form = FormTable(
				FormTableRow(
					"Type:",
					Radio(
						model.isStandardType.generateProperties(),
						i => state.dispatch(SetIsStandardType(i))
					)
				).withKey(key = "type"),
				toOption(standardType)(
					FormTableRow(
						"Study:",
						Select(model.study.generateProperties(), emWidth = Some(boxWidth), onSelectionChange = Some(() => state.dispatch(StudyChange)))
					).withKey(key = "study")
				),
				toOption(standardType && studySelected) {
					val summary = model.subject match {
						case None => ""
						case Some(s) => s.identity.map(_.summary).getOrElse(s.reportingId.noBracketsString) + " "
					}
					FormTableRow(
						"Subject:",
						span(
							summary,
							Button(caption = "select", size = ButtonSize.Small, onClick = () => state.dispatch(SelectSubject))
						)
					).withKey(key = "sub")
				},
				toOption((!standardType || studySelected) && model.enteredNameRequired)(
					FormTableRow(
						"Subject Name (local):",
						Edit(model.localSubjectName, emWidth = boxWidth)
					).withKey(key = "name")
				),
				toOption(standardType && studySelected && (model.previousSessions.isDefined || model.studySessions.isDefined))(
					FormTableRow(
						"Previous Sessions:",
						model.studySessions match {
							case Some(history) => SessionsHistoryForm(
								history = history,
								resumableSubjectGuids = model.resumableSubjectGuids,
								onResumeClick = s => resumeSession(s)
							)
							case None =>
								model.previousSessions match {
									case Some(s) => PreviousSessionsForm(
										sessions = s,
										resumableSubjectGuids = model.resumableSubjectGuids,
										onResumeClick = s => resumeSession(s)
									)
								}
						}
					).withKey(key = "ps")
				),
				toOption(standardType && model.studySessions.isDefined) {
					val suggestion = model.studySessions.flatMap(_.suggestion.flatMap(_.studySession.map(_.studySession)))
					val suggestionCaption = for {
						s <- suggestion
						selection <- model.session.value
						if selection.id != s.id
					} yield s"Suggestion: ${s.description}"
					val sel = SelectControlled(model.session.generateProperties(), emWidth = Some(boxWidth), onSelectionChange = () => state.dispatch(SessionChange))
					FormTableRow(
						"Session:",
						suggestionCaption match {
							case None => sel
							case Some(caption) =>
								LayoutTable(gapSize = GapSize.Small, fullWidth = false)(
									LTRow(
										LTCell(align = HoriAlign.Left)(sel),
										LTCell(align = HoriAlign.Left)(span(style := StyleUtil.makeStyle(("color", "red")))(caption))
									)
								)
						}
					).withKey(key = "sess")
				},
				toOption(standardType && model.session.value.isDefined && model.groupSettings.isEmpty) {
					val s = model.session.value.get
					FormTableRow(
						"",
						span(style := StyleUtil.makeStyle(("color", "red")))(s"The test package being used by this Test Station does not contain the sequence (${s.group_name},${s.sequence_name}) required by the selected session. Either upgrade the test package, or use a different session (if possible.)")
					).withKey(key = "missing_sequence")
				},
				toOption(!standardType)(
					FormTableRow(
						"Group:",
						Select(model.group.generateProperties(), emWidth = Some(boxWidth), onSelectionChange = Some(() => state.dispatch(GroupChange)))
					).withKey(key = "group")
				),
				toOption(!standardType)(
					model.groupSettings map { g =>
						toOption(!standardType)(
							FormTableRow(
								"Sequence:",
								SelectControlled(g.sequence.generateProperties(), emWidth = Some(boxWidth), onSelectionChange = () => state.dispatch(ParameterChange))
							).withKey(key = "seq")
						)
					}
				),
				toOption(!standardType || (studySelected && model.session.value.isDefined))(
					model.groupSettings match {
						case None => Seq()
						case Some(settings) =>
							settings.parameters map { p =>
								FormTableRow(
									p._1.name + ":",
									ParameterSelectView(groupName = settings.name, parm = p._1, select = p._2, history = model.studySessions, dispatcher = state.dispatcher)
								).withKey(key = p._1.name)
							}
					}
				),
				toOption(!standardType || (studySelected && model.session.value.isDefined && model.groupSettings.isDefined))(
					FormTableRow(
						"Location Type:",
						Select(model.locationType.generateProperties(), emWidth = Some(boxWidth))
					).withKey(key = "locationType")
				)
			)

			div(
				model.actionPendingDataPassword map { pending =>
					DataPasswordDialog(
						server = props.resources.server,
						accountId = props.resources.adminUser.account.id,
						userAccountId = props.resources.adminUser.account.id,
						onFinished = pw => {
							val action = if (pw.isDefined) ActionBatch(SetDataPassword(pw), pending) else SetDataPassword(pw)
							state.dispatch(action)
						}
					)
				},
				toOption(model.selectingSubject)(
					ModalContainer(
						SessionSubjectSelectDialog.View(
							server = props.resources.server,
							accountId = props.resources.adminUser.account.id,
							dataPassword = props.resources.dataPassword.getOrElse(""),
							onFinished = res => state.dispatch(SetSubject(res))
						)
						)
				),
				Panel(fullWidth = false, dark = false)(
					FormDialog(
						title = Some("Configure New Session"),
						submitButtonCaption = "Start",
						customView = Some(form),
						process = () => process(),
						onSuccess = res => onSuccess(res)
					)
				)
			)
		}
	}

	private val boxWidth = 20
}
